import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Col, Row } from '../../lib/utils';
import { colors } from '../../lib/colors';
import ReactPlayer from 'react-player';
import { HoverImage } from '../../lib/styled';
import { I_LEFT_BRACKET, I_POPUP_EXIT, I_RIGHT_BRACKET } from '../../types/images';
import Scrollbars from 'react-custom-scrollbars-2';

const FullscreenOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

type Props = {
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    videoList: any[];
    selectVideoId: string;
    isIOSiPad?: () => boolean;
};

const ModalVideoPlayer = ({ setModalVisible, videoList, selectVideoId, isIOSiPad }: Props) => {
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const videoRef = React.useRef<HTMLVideoElement>(null);

    const checkIsIOSiPad = () => {
        if (isIOSiPad) {
            return isIOSiPad();
        }
        const userAgent = navigator.userAgent.toLowerCase();
        const isIPad = /ipad/.test(userAgent);
        const isIOS = /iphone|ipad|ipod/.test(userAgent);
        return isIPad || (isIOS && window.innerWidth >= 768);
    };

    const handleVideoPlay = () => {
        if (checkIsIOSiPad() && videoRef.current) {
            videoRef.current.play().catch((error) => {
                console.log('Video play error:', error);
            });
            setIsPlaying(true);
        }
    };

    useEffect(() => {
        let findIndex = videoList.findIndex((item: any) => item._id === selectVideoId);
        findIndex && setCurrentIndex(findIndex);
    }, []);

    // ESC 키로 닫기
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setModalVisible(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const onClickChangePicture = (index: number) => {
        setCurrentIndex(index);
    };

    const onClickNextImage = () => {
        if (currentIndex > videoList.length - 2) {
            return;
        } else {
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const onClickPrevImage = () => {
        if (currentIndex === 0) {
            return;
        } else {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    return (
        <FullscreenOverlay onClick={() => setModalVisible(false)}>
            <div
                className="flex items-center justify-center w-full h-full"
                onClick={(e) => e.stopPropagation()}
            >
                {/* 닫기 버튼 */}
                <HoverImage
                    src={I_POPUP_EXIT}
                    style={{
                        width: 40,
                        height: 40,
                        position: 'absolute',
                        top: 24,
                        right: 24,
                        filter: 'brightness(10)',
                        cursor: 'pointer',
                        zIndex: 10,
                    }}
                    onClick={() => setModalVisible(false)}
                />

                {/* 이전 버튼 */}
                <div
                    onClick={onClickPrevImage}
                    className="flex items-center justify-center cursor-pointer absolute left-[24px]"
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        backgroundColor: currentIndex > 0 ? 'rgba(255,255,255,0.15)' : 'transparent',
                        transition: 'background 0.2s',
                        zIndex: 10,
                    }}
                >
                    {currentIndex > 0 && (
                        <HoverImage src={I_LEFT_BRACKET} style={{ width: 14, height: 44, filter: 'brightness(10)' }} />
                    )}
                </div>

                {/* 메인 비디오 */}
                <div
                    className="flex items-center justify-center"
                    style={{ width: 'calc(100vw - 220px)', height: 'calc(100vh - 80px)' }}
                >
                    {videoList[currentIndex]?.content &&
                        (checkIsIOSiPad() ? (
                            <div
                                style={{ width: '100%', height: '100%', position: 'relative' }}
                                onClick={handleVideoPlay}
                            >
                                <video
                                    ref={videoRef}
                                    src={videoList[currentIndex].content}
                                    controls
                                    muted
                                    playsInline
                                    preload="metadata"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }}
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                />
                            </div>
                        ) : (
                            <ReactPlayer
                                url={videoList[currentIndex].content}
                                muted
                                controls
                                width={'100%'}
                                height={'100%'}
                                style={{ borderRadius: 8, overflow: 'hidden' }}
                            />
                        ))}
                </div>

                {/* 다음 버튼 */}
                <div
                    onClick={onClickNextImage}
                    className="flex items-center justify-center cursor-pointer absolute right-[120px]"
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        backgroundColor: currentIndex < videoList.length - 1 ? 'rgba(255,255,255,0.15)' : 'transparent',
                        transition: 'background 0.2s',
                        zIndex: 10,
                    }}
                >
                    {currentIndex < videoList.length - 1 && (
                        <HoverImage src={I_RIGHT_BRACKET} style={{ width: 14, height: 44, filter: 'brightness(10)' }} />
                    )}
                </div>

                {/* 우측 썸네일 사이드바 */}
                <div
                    className="absolute right-0 top-0 h-full flex flex-col items-center pt-[80px] pb-[24px]"
                    style={{ width: 100, backgroundColor: 'rgba(0,0,0,0.4)' }}
                >
                    {/* 카운터 */}
                    <div className="text-white text-[13px] mb-[12px] opacity-70">
                        {currentIndex + 1} / {videoList.length}
                    </div>

                    <Scrollbars
                        autoHide
                        autoHideTimeout={800}
                        autoHideDuration={200}
                        style={{ width: 92, flex: 1 }}
                    >
                        <Col style={{ width: 76, paddingLeft: 8 }}>
                            {videoList &&
                                videoList.map((item: any, index: number) => {
                                    return (
                                        <Row
                                            key={'videoDetail' + index}
                                            style={{
                                                width: 68,
                                                height: 68,
                                                marginTop: index === 0 ? 4 : 0,
                                                marginBottom: 8,
                                                borderRadius: 6,
                                                overflow: 'hidden',
                                                border: currentIndex === index ? '3px solid #3D73DD' : '3px solid transparent',
                                                cursor: 'pointer',
                                                opacity: currentIndex === index ? 1 : 0.6,
                                                transition: 'all 0.2s',
                                            }}
                                            onClick={() => onClickChangePicture(index)}
                                        >
                                            <img src={item?.thumbnail} style={{ width: 68, height: 68, objectFit: 'cover' }} />
                                        </Row>
                                    );
                                })}
                        </Col>
                    </Scrollbars>
                </div>
            </div>
        </FullscreenOverlay>
    );
};

export default ModalVideoPlayer;
